import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight, ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'

type ButtonVariant = 'primary' | 'secondary' | 'quiet'
type ButtonSize = 'md' | 'lg'
type ButtonIcon = 'arrow' | 'external' | 'none'

interface ButtonProps {
    children: ReactNode
    href?: string
    variant?: ButtonVariant
    size?: ButtonSize
    icon?: ButtonIcon
    type?: 'button' | 'submit' | 'reset'
    disabled?: boolean
    onClick?: () => void
    className?: string
    ariaLabel?: string
}

const BASE = 'group/button inline-flex items-center justify-center gap-2.5 rounded-sm font-sans font-medium whitespace-nowrap transition-[background-color,color,border-color,text-decoration-color] duration-200 ease-out active:translate-y-px disabled:cursor-not-allowed disabled:opacity-50'

const VARIANTS: Record<ButtonVariant, string> = {
    primary: 'bg-primary text-primary-foreground hover:bg-brand hover:text-brand-foreground',
    secondary: 'border border-foreground text-foreground hover:bg-foreground hover:text-background',
    quiet: 'px-0 text-foreground underline decoration-1 underline-offset-[0.35em] decoration-foreground/35 hover:decoration-foreground'
}

const SIZES: Record<ButtonSize, string> = {
    md: 'min-h-11 px-5 text-[0.9375rem]',
    lg: 'min-h-12 px-6 text-base'
}

const isExternal = (href: string): boolean => /^(https?:)?\/\//.test(href)

const Button: FC<ButtonProps> = ({
    children,
    href,
    variant = 'primary',
    size = 'md',
    icon = 'none',
    type = 'button',
    disabled = false,
    onClick,
    className,
    ariaLabel
}): ReactNode => {
    const classes = cn(BASE, SIZES[size], VARIANTS[variant], className)

    const content = (
        <>
            <span>{children}</span>

            {icon === 'arrow' && (
                <ArrowRight
                    aria-hidden='true'
                    className='size-4 transition-transform duration-300 ease-editorial group-hover/button:translate-x-0.5'
                />
            )}

            {icon === 'external' && (
                <ArrowUpRight
                    aria-hidden='true'
                    className='size-4 transition-transform duration-300 ease-editorial group-hover/button:-translate-y-0.5 group-hover/button:translate-x-0.5'
                />
            )}
        </>
    )

    if (href && isExternal(href)) {
        return (
            <a href={href} target='_blank' rel='noopener noreferrer' className={classes} aria-label={ariaLabel}>
                {content}
            </a>
        )
    }

    if (href && href.startsWith('mailto:')) {
        return (
            <a href={href} className={classes} aria-label={ariaLabel}>
                {content}
            </a>
        )
    }

    if (href) {
        return (
            <Link href={href} className={classes} aria-label={ariaLabel} onClick={onClick}>
                {content}
            </Link>
        )
    }

    return (
        <button type={type} disabled={disabled} onClick={onClick} className={classes} aria-label={ariaLabel}>
            {content}
        </button>
    )
}

export default Button
